import {
    BackgroundCheckError,
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '@/backend/features/background-check';
import { BackgroundCheckConfig, BackgroundCheckQuota, BackgroundCheckTest } from '@/backend/features/background-check/defaults';
import { getCleanedSsn } from '@/backend/features/background-check/platform-functions/utils/backgroundCheckUtils';
import { validateIntellicorpRequest } from '@/backend/features/background-check/platform-functions/utils/validateIntellicorpRequest';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus, BackgroundCheckType, IntellicorpBackgroundCheckType } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { BackgroundCheckRequest, BackgroundCheckResponse } from '@juvare/platform-app/Integration';
import { AppState } from '@juvare/platform-sdk-react/types';

export interface RequestIntellicorpBackgroundCheckParams {
    requestedBy: string;
    context: AppContext<AppState>;
    config: BackgroundCheckConfig;
    accountData: ExtendedAccountData;
    quota: BackgroundCheckQuota;
    backgroundCheckTestConfig: BackgroundCheckTest;
    backgroundCheckType: IntellicorpBackgroundCheckType;
    accountId: string;
    backgroundCheck: BackgroundCheckColumns;
    isTestMode: boolean;
}

export const requestIntellicorpBackgroundCheck = ({
    requestedBy,
    context,
    config,
    accountData,
    quota,
    backgroundCheckTestConfig,
    backgroundCheckType,
    accountId,
    backgroundCheck,
    isTestMode,
}: RequestIntellicorpBackgroundCheckParams) => {
    const validateResponse = validateIntellicorpRequest({
        context,
        accountData,
        backgroundCheck,
        backgroundCheckType,
        config,
        quota,
    });

    const screeningApi = context.screening;

    const request: BackgroundCheckRequest = {
        client: config.client,
        type: validateResponse.jasType,
        ...validateResponse.person,
    };

    if (backgroundCheckTestConfig.useTestData && backgroundCheckTestConfig.testData) {
        request.test = {
            results: {
                productIds: backgroundCheckTestConfig.testData,
            },
        };
    }

    try {
        const response = screeningApi.requestBackgroundCheck(request);
        const sanitizedRequest = { ...request, ssn: getCleanedSsn(request.ssn) };

        return createHistory({
            context,
            backgroundCheckType,
            accountId,
            request: sanitizedRequest,
            backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
            response,
            message: response.message,
            nextStatus: response.executionId ? 'processingPending' : 'processingError',
            isUsingQuota: validateResponse.isUsingQuota,
            isManualChange: true,
            isTestMode,
            requestedBy,
        });
    } catch (e) {
        if (e instanceof BackgroundCheckError) {
            try {
                createHistory({
                    context,
                    backgroundCheckType,
                    accountId,
                    backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
                    nextStatus: e.backgroundCheckStatus,
                    message: e.message,
                    isTestMode,
                    requestedBy,
                });
            } catch (e) {
                console.error(e);
            }

            throw e;
        } else {
            try {
                createHistory({
                    context,
                    backgroundCheckType,
                    accountId,
                    backgroundCheckStatus: backgroundCheck.status as BackgroundCheckStatus,
                    nextStatus: 'processingError',
                    message: e instanceof Error ? e.message : 'Unknown error',
                    isTestMode,
                    requestedBy,
                });
            } catch (e) {
                console.error(e);
            }

            throw e;
        }
    }
};

interface HistoryParams {
    requestedBy: string;
    context: AppContext<AppState>;
    backgroundCheckType: BackgroundCheckType;
    accountId: string;
    backgroundCheckStatus: BackgroundCheckStatus;
    response?: BackgroundCheckResponse;
    request?: BackgroundCheckRequest;
    nextStatus: BackgroundCheckStatus;
    message?: string;
    isUsingQuota?: boolean;
    isManualChange?: boolean;
    isTestMode: boolean;
}

function createHistory({
    context,
    requestedBy,
    backgroundCheckType,
    accountId,
    backgroundCheckStatus,
    response,
    request,
    nextStatus,
    message,
    isUsingQuota,
    isManualChange,
    isTestMode,
}: HistoryParams) {
    const storage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(backgroundCheckHistoryEntityName);
    storage.post({
        action: 'create',
        data: {
            requestedBy: requestedBy,
            accountId,
            type: backgroundCheckType,
            prevStatus: backgroundCheckStatus ?? undefined,
            nextStatus,
            request,
            requestId: response?.executionId,
            checkResponseByRequestId: response?.executionId,
            message: message ?? response?.message,
            isUsingQuota: isUsingQuota,
            isManualChange: isManualChange,
            isTestMode,
        },
    });
}
